/**
 * Element-Based Visual Editor — Types
 *
 * Each UI element can have per-element style overrides keyed by a unique
 * `data-design-id`. Overrides layer on top of the existing token system.
 *
 * ARCHITECTURE: All styles are applied via React render (getElementStyle),
 * NOT via DOM manipulation. No MutationObservers, no querySelectorAll.
 */

/** Style properties that can be overridden per element */
export interface ElementStyle {
  backgroundColor?: string; // HEX
  textColor?: string;       // HEX
  iconColor?: string;       // HEX
  borderColor?: string;     // HEX
  borderWidth?: string;     // px value e.g. "2"
}

/** Full override map: elementId → { light, dark } */
export type ElementOverrideMap = Record<string, {
  light: ElementStyle;
  dark: ElementStyle;
}>;

/** What kind of element was selected — determines which controls to show */
export type ElementCategory =
  | "card"        // bg, text, icon, border
  | "background"  // bg only
  | "button"      // bg, text, border
  | "text"        // text only
  | "icon"        // icon only
  | "nav"         // bg, text, icon, border
  | "generic";    // all

/** Metadata about a selectable element */
export interface SelectedElement {
  designId: string;
  label: string;         // Human-readable name shown in the panel
  category: ElementCategory;
  element: HTMLElement;   // The design-id container (for override persistence)
  actualElement?: HTMLElement; // The innermost element that was actually clicked
  computedColors?: ElementStyle; // Pre-filled from getComputedStyle of the actual clicked element
}

/** Design IDs that are too generic to use for specific element picks */
const GENERIC_DESIGN_IDS = new Set(["page-bg"]);

/** Convert rgb/rgba string to HEX */
export function rgbToHex(rgb: string): string {
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return "";
  const [r, g, b] = match.map(Number);
  if (rgb.startsWith("rgba") && match.length >= 4 && Number(match[3]) === 0) return "";
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** Extract computed colors from a DOM element */
export function extractComputedColors(el: HTMLElement): ElementStyle {
  const cs = getComputedStyle(el);
  const bg = rgbToHex(cs.backgroundColor);
  const text = rgbToHex(cs.color);
  const border = cs.borderWidth !== "0px" ? rgbToHex(cs.borderColor) : undefined;
  const svg = el.tagName === "svg" ? el : el.querySelector("svg");
  const icon = svg ? rgbToHex(getComputedStyle(svg).color) : undefined;
  return {
    ...(bg ? { backgroundColor: bg } : {}),
    ...(text ? { textColor: text } : {}),
    ...(border ? { borderColor: border } : {}),
    ...(icon ? { iconColor: icon } : {}),
  };
}

/** Generate a human-readable label from an arbitrary element */
export function generateElementLabel(el: HTMLElement): string {
  const text = el.textContent?.trim().slice(0, 20);
  const tag = el.tagName.toLowerCase();
  if (tag === "svg" || el.closest("svg")) return "אייקון";
  if (tag === "img") return el.getAttribute("alt") || "תמונה";
  if (tag === "button" || el.closest("button")) return text ? `כפתור: ${text}` : "כפתור";
  if (tag === "a") return text ? `קישור: ${text}` : "קישור";
  if (tag === "h1" || tag === "h2" || tag === "h3") return text ? `כותרת: ${text}` : "כותרת";
  if (tag === "span" || tag === "p" || tag === "label") return text ? `טקסט: ${text}` : "טקסט";
  if (text) return text;
  return tag;
}

/** Detect category from an arbitrary element (not just by designId) */
export function detectElementCategory(el: HTMLElement): ElementCategory {
  const tag = el.tagName.toLowerCase();
  if (tag === "svg" || el.closest("svg")) return "icon";
  if (tag === "img") return "background";
  if (tag === "button" || el.closest("button")) return "button";
  if (["span", "p", "h1", "h2", "h3", "h4", "label"].includes(tag)) return "text";
  return "generic";
}

/** Which controls to show for each category */
export const CATEGORY_CONTROLS: Record<ElementCategory, (keyof ElementStyle)[]> = {
  card: ["backgroundColor", "textColor", "iconColor", "borderColor", "borderWidth"],
  background: ["backgroundColor"],
  button: ["backgroundColor", "textColor", "borderColor", "borderWidth"],
  text: ["textColor"],
  icon: ["iconColor"],
  nav: ["backgroundColor", "textColor", "iconColor", "borderColor", "borderWidth"],
  generic: ["backgroundColor", "textColor", "iconColor", "borderColor", "borderWidth"],
};

/** Hebrew labels for style properties */
export const STYLE_LABELS: Record<keyof ElementStyle, string> = {
  backgroundColor: "צבע רקע",
  textColor: "צבע טקסט",
  iconColor: "צבע אייקון",
  borderColor: "צבע גבול",
  borderWidth: "עובי מסגרת",
};

/** Predefined design IDs and their labels */
export const ELEMENT_LABELS: Record<string, string> = {
  // Home cards
  "home-card-communication": "כרטיס תקשורת",
  "home-card-keyboard": "כרטיס מקלדת",
  "home-card-entertainment": "כרטיס בידור",
  "home-card-rest": "כרטיס מנוחה",
  "home-card-help": "כרטיס עזרה",
  // Entertainment cards
  "ent-card-youtube": "כרטיס יוטיוב",
  "ent-card-tv": "כרטיס טלוויזיה",
  "ent-card-music": "כרטיס מוזיקה",
  "ent-card-radio": "כרטיס רדיו",
  // Backgrounds
  "page-bg": "רקע העמוד",
  // Sidebar
  "sidebar": "סרגל צד",
  "sidebar-item-settings": "כפתור הגדרות",
  "sidebar-item-help": "כפתור עזרה",
  // Status bar
  "status-bar": "שורת סטטוס",
  // Radio cards
  "radio-country-card": "כרטיס מדינה",
  "radio-genre-card": "כרטיס ז'אנר",
  "radio-station-card": "כרטיס תחנה",
  // Emergency cells
  "emergency-cell": "כרטיס חירום",
  // Help page
  "help-main-icon": "אייקון עזרה ראשי",
  // AAC Keyboard
  "aac-text-display": "שדה הקלדה",
};

/**
 * Design ID groups — elements that can be edited together.
 */
export interface DesignIdGroup {
  label: string;
  memberIds: string[];
}

export const DESIGN_ID_GROUPS: DesignIdGroup[] = [
  {
    label: "כל כרטיסיות הרדיו",
    memberIds: ["radio-country-card", "radio-genre-card", "radio-station-card"],
  },
  {
    label: "כל כרטיסיות הבית",
    memberIds: ["home-card-communication", "home-card-keyboard", "home-card-entertainment", "home-card-rest", "home-card-help"],
  },
  {
    label: "כל כרטיסיות הבידור",
    memberIds: ["ent-card-youtube", "ent-card-tv", "ent-card-music", "ent-card-radio"],
  },
];

/** Find the group a design ID belongs to (if any) */
export function findGroupForDesignId(designId: string): DesignIdGroup | undefined {
  return DESIGN_ID_GROUPS.find(g => g.memberIds.includes(designId));
}

/** Infer element category from data-design-id */
export function inferCategory(designId: string): ElementCategory {
  if (designId.startsWith("home-card-")) return "card";
  if (designId.startsWith("ent-card-")) return "card";
  if (designId.startsWith("page-bg")) return "background";
  if (designId.startsWith("sidebar-item-")) return "button";
  if (designId.startsWith("sidebar")) return "nav";
  if (designId.startsWith("status-bar")) return "nav";
  if (designId.startsWith("nav-")) return "nav";
  if (designId.startsWith("comm-cell-")) return "card";
  if (designId.startsWith("btn-")) return "button";
  if (designId.startsWith("radio-")) return "card";
  if (designId.startsWith("emergency-")) return "card";
  return "generic";
}

/** Check if a design-id is too generic for targeting specific sub-elements */
export function isGenericDesignId(designId: string): boolean {
  return GENERIC_DESIGN_IDS.has(designId);
}

export const ELEMENT_OVERRIDES_STORAGE_KEY = "ayuni-element-overrides";
